import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { artefactPath, currentArtefactPath, mutedStyle, useToolApi } from './toolApi';

export default function ReleaseArtefact({ release, artefact, currentToolDocumentNumber }) {
  const api = useToolApi();
  const { userId, sessionId } = useAuth();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const current = useRef(0);
  const controller = useRef(null);
  const downloadUrl = useRef(null);
  const revokeTimer = useRef(null);
  useEffect(() => {
    current.current += 1; setError(''); setBusy(false); setReceipt(null);
    return () => {
      current.current += 1; controller.current?.abort(); clearTimeout(revokeTimer.current);
      if (downloadUrl.current) URL.revokeObjectURL(downloadUrl.current);
      downloadUrl.current = null;
    };
  }, [release.id, artefact.name, currentToolDocumentNumber, userId, sessionId]);
  const download = async () => {
    const generation = ++current.current;
    controller.current?.abort(); controller.current = new AbortController();
    setBusy(true); setError(''); setReceipt(null);
    let url;
    try {
      const path = currentToolDocumentNumber ? currentArtefactPath(currentToolDocumentNumber, artefact.name) : artefactPath(release.id, artefact.name);
      const result = await api(path, { signal: controller.current.signal }, 'download');
      if (generation !== current.current) return;
      if (currentToolDocumentNumber && (!result.filename || !result.version || !result.releaseId || !/^[a-f0-9]{64}$/i.test(result.sha256))) {
        throw new Error('The current download could not be identified. Reload the catalogue before trying again.');
      }
      url = URL.createObjectURL(result.blob);
      downloadUrl.current = url;
      const anchor = document.createElement('a');
      anchor.href = url; anchor.download = result.filename || artefact.file || artefact.name; document.body.appendChild(anchor); anchor.click(); anchor.remove();
      if (currentToolDocumentNumber) setReceipt({ filename: result.filename, version: result.version, releaseId: result.releaseId, sha256: result.sha256 });
    } catch (reason) { if (generation === current.current && reason.name !== 'AbortError') setError(reason.message); }
    finally {
      if (url) revokeTimer.current = setTimeout(() => { URL.revokeObjectURL(url); if (downloadUrl.current === url) downloadUrl.current = null; }, 1000);
      if (generation === current.current) setBusy(false);
    }
  };
  return <div style={{ padding: '.7rem 0', borderTop: '1px solid var(--ni-border-soft)' }}>
    <strong>{artefact.name}</strong>{artefact.file && artefact.file !== artefact.name ? ` · ${artefact.file}` : ''} · {artefact.size ?? 'Unknown'} bytes
    <p style={{ ...mutedStyle, overflowWrap: 'anywhere', margin: '.4rem 0' }}>SHA256: <code>{artefact.sha256 || 'Unavailable'}</code></p>
    <button className="ni-button" type="button" disabled={busy || release.status === 'Withdrawn' || !artefact.sha256} onClick={download}>
      {busy ? 'Downloading…' : currentToolDocumentNumber ? `Download current ${artefact.name}` : `Download ${artefact.file || artefact.name}`}
    </button>
    {error && <p role="alert">{error}</p>}
    {receipt && <p role="status" style={{ overflowWrap: 'anywhere' }}>Downloaded current version {receipt.version}: {receipt.filename} · SHA256 <code>{receipt.sha256}</code></p>}
  </div>;
}
